import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import SchemaApi from '../../api/SchemaApi';
import ObjectConfigApi from '../../api/ObjectConfigApi';
import { getEntitlementsSection } from '../../utils/entitlements';

const EditObject = ({ show, onHide, mode = 'create', objectData = null, onSuccess }) => {
  const objectManagerConfig = getEntitlementsSection('objectManager', {});
  const allowStandardObject =
    objectManagerConfig?.enable_standard ??
    objectManagerConfig?.enableStandard ??
    true;
  const [formData, setFormData] = useState({
    objectName: '',
    objectLabel: '',
    pluralLabel: '',
    nameFieldLabel: 'Name', // Label for the name field
    nameFieldType: 'text', // text or autonumber
    nameStartNumber: 1,
    namePrefix: '',
    nameFormatMask: '0000',
    allowActivity: false,
    allowEmailLog: false,
    allowNotes: false,
    createTab: false,
    isStandard: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [nameFieldInfo, setNameFieldInfo] = useState(null);
  const [initialAllowEmailLog, setInitialAllowEmailLog] = useState(false);
  const [initialAllowNotes, setInitialAllowNotes] = useState(false);

  // Reset form when modal opens/closes or mode changes
  useEffect(() => {
    if (show) {
      if (mode === 'edit' && objectData) {
        console.log('EditObject - objectData received:', objectData);
        console.log('EditObject - allow_activity value:', objectData.allow_activity, 'type:', typeof objectData.allow_activity);
        const allowEmailLogValue = objectData.allow_email_log || false;
        const allowNotesValue = objectData.allow_notes || false;

        setInitialAllowEmailLog(allowEmailLogValue);
        setInitialAllowNotes(allowNotesValue);

        setFormData({
          objectName: objectData.object_name || '',
          objectLabel: objectData.object_label || '',
          pluralLabel: objectData.plural_label || '',
          allowActivity: objectData.allow_activity || false,
          allowEmailLog: allowEmailLogValue,
          allowNotes: allowNotesValue,
          createTab: false, // Always false for edit mode
          isStandard: objectData.is_standard || false
        });

        // Fetch name field configuration for edit mode
        fetchNameFieldInfo(objectData.object_name);
      } else {
        setFormData({
          objectName: '',
          objectLabel: '',
          pluralLabel: '',
          nameFieldLabel: 'Name',
          nameFieldType: 'text',
          nameStartNumber: 1,
          namePrefix: '',
          nameFormatMask: '0000',
          allowActivity: false,
          allowEmailLog: false,
          allowNotes: false,
          createTab: false,
          isStandard: false
        });
        setNameFieldInfo(null);
        setInitialAllowEmailLog(false);
        setInitialAllowNotes(false);
      }
      setError('');
      setSuccess('');
    }
  }, [show, mode, objectData]);

  // Fetch name field information for edit mode
  const fetchNameFieldInfo = async (objectName) => {
    try {
      const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);

      // Handle both response.data.fields and response.fields structures
      const fields = response?.data?.fields || response?.fields;

      if (fields && Array.isArray(fields)) {
        const nameField = fields.find(f => f.field_name === 'name');

        if (nameField) {
          const fieldInfo = {
            label: nameField.field_label || 'Name',
            dataType: nameField.data_type,
            prefix: nameField.prefix !== undefined && nameField.prefix !== null ? nameField.prefix : '',
            startNumber: nameField.start_number || 1,
            formatMask: nameField.format_mask !== undefined && nameField.format_mask !== null ? nameField.format_mask : '',
            preview: nameField.data_type === 'autonumber'
              ? generateAutonumberPreview(
                nameField.prefix !== undefined && nameField.prefix !== null ? nameField.prefix : '',
                nameField.format_mask !== undefined && nameField.format_mask !== null ? nameField.format_mask : '',
                nameField.start_number || 1
              )
              : null
          };
          setNameFieldInfo(fieldInfo);
        }
      }
    } catch (error) {
      console.error('Error fetching name field info:', error);
    }
  };

  // Function to generate object name from label
  const generateObjectName = (label) => {
    if (!label) return '';

    // Convert to lowercase, replace spaces and special characters with underscores
    let objectName = label
      .toLowerCase()
      .replace(/[^a-zA-Z0-9\s]/g, '_') // Replace special characters with underscore
      .replace(/\s+/g, '_') // Replace spaces with underscore
      .replace(/_+/g, '_') // Replace multiple underscores with single underscore
      .replace(/^_|_$/g, ''); // Remove leading and trailing underscores

    // Ensure it starts with a letter or underscore
    if (objectName && !/^[a-zA-Z_]/.test(objectName)) {
      objectName = '_' + objectName;
    }

    return objectName;
  };

  // Function to pluralize a word
  const pluralizeWord = (word) => {
    if (!word) return '';

    const lowercaseWord = word.toLowerCase();

    // Words that don't change in plural
    const uncountable = ['equipment', 'information', 'rice', 'money', 'species', 'series', 'fish', 'sheep', 'data'];
    if (uncountable.includes(lowercaseWord)) {
      return word;
    }

    // Irregular plurals
    const irregulars = {
      'person': 'People',
      'child': 'Children',
      'man': 'Men',
      'woman': 'Women',
      'tooth': 'Teeth',
      'foot': 'Feet',
      'mouse': 'Mice',
      'goose': 'Geese'
    };
    if (irregulars[lowercaseWord]) {
      return irregulars[lowercaseWord];
    }

    // Already ends with 's', 'ss', 'x', 'z', 'ch', 'sh' - add 'es'
    if (/(?:s|ss|x|z|ch|sh)$/i.test(word)) {
      return word + 'es';
    }

    // Ends with consonant + 'y' - replace 'y' with 'ies'
    if (/[^aeiou]y$/i.test(word)) {
      return word.slice(0, -1) + 'ies';
    }

    // Ends with 'f' or 'fe' - replace with 'ves'
    if (/fe?$/i.test(word)) {
      return word.replace(/fe?$/i, 'ves');
    }

    // Ends with consonant + 'o' - add 'es'
    if (/[^aeiou]o$/i.test(word)) {
      return word + 'es';
    }

    // Default: just add 's'
    return word + 's';
  };

  // Generate autonumber preview based on prefix and format mask
  const generateAutonumberPreview = (prefix = '', formatMask = '', startNumber = 1) => {
    let preview = prefix || '';

    if (formatMask) {
      // Replace format mask with padded number
      const numberStr = startNumber.toString();
      let formattedNumber = numberStr;

      // If format mask has more digits than the number, pad with zeros
      if (formatMask.length > numberStr.length) {
        formattedNumber = numberStr.padStart(formatMask.length, '0');
      }

      preview += formattedNumber;
    } else {
      preview += startNumber.toString();
    }

    return preview || '1';
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'objectLabel' && mode === 'create') {
      // Auto-generate object name and plural label when label changes in create mode
      const generatedObjectName = generateObjectName(value);
      const generatedPluralLabel = pluralizeWord(value);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        objectName: generatedObjectName,
        pluralLabel: generatedPluralLabel
      }));
    } else if (name === 'allowActivity') {
      // When allow_activity is checked, automatically enable allow_email_log and allow_notes
      // When unchecked, also uncheck allow_email_log and allow_notes
      setFormData(prev => ({
        ...prev,
        allowActivity: checked,
        allowEmailLog: checked,
        allowNotes: checked
      }));
    } else if (name === 'objectName') {
      // Object API name is always stored and displayed in lowercase
      setFormData(prev => ({
        ...prev,
        [name]: typeof value === 'string' ? value.trim().toLowerCase() : value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const validateForm = () => {
    if (!formData.objectName.trim()) {
      setError('Object name is required');
      return false;
    }
    if (!formData.objectLabel.trim()) {
      setError('Object label is required');
      return false;
    }

    // Validate object name format (only letters, numbers, underscores, must start with letter or underscore)
    const nameRegex = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
    if (!nameRegex.test(formData.objectName)) {
      setError('Object name must start with a letter or underscore and contain only letters, numbers, and underscores');
      return false;
    }

    // Do not allow __x in object name when creating; backend adds it automatically for custom objects
    if (mode === 'create' && formData.objectName.includes('__x')) {
      setError('Object name must not contain __x; it is added automatically for custom objects.');
      return false;
    }

    setError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (mode === 'create') {
        // Object API name is always lowercase; add __x suffix for custom objects (non-standard)
        let objectNameToUse = (formData.objectName || '').trim().toLowerCase();
        if (!formData.isStandard && objectNameToUse && !objectNameToUse.endsWith('__x')) {
          objectNameToUse = objectNameToUse + '__x';
        }

        const response = await SchemaApi.createObject(
          objectNameToUse,
          formData.objectLabel,
          formData.pluralLabel,
          formData.nameFieldLabel,
          formData.nameFieldType,
          formData.nameStartNumber,
          formData.namePrefix,
          formData.nameFormatMask,
          formData.allowActivity,
          formData.allowEmailLog,
          formData.allowNotes,
          formData.createTab,
          allowStandardObject ? formData.isStandard : false
        );

        if (response.success) {
          setSuccess('Object created successfully!');
          setTimeout(() => {
            onSuccess && onSuccess(response.data);
            onHide();
          }, 1500);
        } else {
          setError(response.message || 'Failed to create object');
        }
      } else if (mode === 'edit') {
        // Update existing object config
        const updateData = {
          object_label: formData.objectLabel,
          plural_label: formData.pluralLabel,
          is_standard: allowStandardObject
            ? formData.isStandard
            : (objectData?.is_standard || false)
        };

        // Don't include allow_activity, allow_email_log, and allow_notes for email_log object
        // When allow_activity is true, backend will automatically set allow_email_log and allow_notes to true
        if (objectData?.object_name?.toLowerCase() !== 'email_log') {
          updateData.allow_activity = formData.allowActivity;
          // Note: allow_email_log and allow_notes are automatically set by backend when allow_activity is true
        }

        const response = await SchemaApi.updateObjectConfig(objectData.id, updateData);

        if (response.success) {
          setSuccess('Object updated successfully!');
          setTimeout(() => {
            onSuccess && onSuccess(response.data);
            onHide();
          }, 1500);
        } else {
          setError(response.message || 'Failed to update object');
        }
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({ objectName: '', objectLabel: '' });
      setError('');
      setSuccess('');
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="md" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {mode === 'create' ? 'Create New Object' : 'Edit Object'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Object Label *</Form.Label>
            <Form.Control
              type="text"
              name="objectLabel"
              value={formData.objectLabel}
              onChange={handleInputChange}
              placeholder="Enter display label (e.g., Account, Contact)"
              required
            />
            <Form.Text className="text-muted">
              This is the display name that users will see. Plural label and object name will be auto-generated.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Plural Label *</Form.Label>
            <Form.Control
              type="text"
              name="pluralLabel"
              value={formData.pluralLabel}
              onChange={handleInputChange}
              placeholder="Plural form of the object label"
              required
            />
            <Form.Text className="text-muted">
              {mode === 'create'
                ? 'Auto-generated from label. You can edit if needed (e.g., "Contacts" for "Contact").'
                : 'The plural form of the object label for display purposes.'
              }
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Object Name *</Form.Label>
            <Form.Control
              type="text"
              name="objectName"
              value={formData.objectName}
              onChange={handleInputChange}
              placeholder="Auto-generated object name"
              disabled={mode === 'edit'} // Object name cannot be changed in edit mode
              required
            />
            {mode === 'create' && formData.objectName && formData.objectName.includes('__x') && (
              <Form.Text className="text-danger">Object name must not contain __x; it is added automatically for custom objects.</Form.Text>
            )}
            <Form.Text className="text-muted">
              {mode === 'create'
                ? 'Auto-generated from label. You can edit if needed. Must start with a letter or underscore and contain only letters, numbers, and underscores. Do not include __x.'
                : 'Object name cannot be changed in edit mode.'
              }
            </Form.Text>
          </Form.Group>

          {mode === 'create' && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Name Field Label *</Form.Label>
                <Form.Control
                  type="text"
                  name="nameFieldLabel"
                  value={formData.nameFieldLabel}
                  onChange={handleInputChange}
                  placeholder="e.g., Account Name, Contact Name"
                  required
                />
                <Form.Text className="text-muted">
                  Customize the label for the standard "name" field (e.g., "Account Name" for accounts, "Product Name" for products).
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Name Field Type *</Form.Label>
                <Form.Select
                  name="nameFieldType"
                  value={formData.nameFieldType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="text">Text</option>
                  <option value="autonumber">Auto Number</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  Choose the type for the "Name" field. Auto Number will automatically generate sequential numbers. This cannot be changed after creation.
                </Form.Text>
              </Form.Group>

              {formData.nameFieldType === 'autonumber' && (
                <div className="autonumber-config mb-3 p-3 border rounded bg-light">
                  <h6 className="mb-3">Auto Number Configuration</h6>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Start Number *</Form.Label>
                        <Form.Control
                          type="number"
                          min={1}
                          value={formData.nameStartNumber}
                          onChange={(e) => {
                            const val = Math.max(1, Number(e.target.value || 1));
                            setFormData({ ...formData, nameStartNumber: val });
                          }}
                          placeholder="Enter starting number (e.g., 1)"
                        />
                        <Form.Text className="text-muted">
                          The first record will get this number, and subsequent records will increment by 1.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Prefix</Form.Label>
                        <Form.Control
                          type="text"
                          name="namePrefix"
                          value={formData.namePrefix}
                          onChange={handleInputChange}
                          placeholder="e.g., ACC-, CUST-, INV-"
                        />
                        <Form.Text className="text-muted">
                          Optional prefix for the autonumber (e.g., ACC-, CUST-).
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Format Mask</Form.Label>
                        <Form.Control
                          type="text"
                          name="nameFormatMask"
                          value={formData.nameFormatMask}
                          onChange={handleInputChange}
                          placeholder="e.g., 0000, 00000"
                        />
                        <Form.Text className="text-muted">
                          Optional format mask to pad numbers (e.g., 0000 makes 1 → 0001).
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Preview</Form.Label>
                        <Form.Control
                          type="text"
                          value={generateAutonumberPreview(
                            formData.namePrefix,
                            formData.nameFormatMask,
                            formData.nameStartNumber || 1
                          )}
                          readOnly
                          className="bg-white"
                          placeholder="Preview will appear here"
                        />
                        <Form.Text className="text-muted">
                          Preview of how the autonumber will look.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              )}
            </>
          )}

          {mode === 'create' && (
            <>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  name="allowActivity"
                  checked={formData.allowActivity}
                  onChange={handleInputChange}
                  label="Allow Activity"
                />
                <Form.Text className="text-muted">
                  When checked, this will enable activity tracking (Tasks, Notes, and Email Logs) for this object.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  name="createTab"
                  checked={formData.createTab}
                  onChange={handleInputChange}
                  label="Create Tab"
                />
                <Form.Text className="text-muted">
                  When checked, this will create a navigation tab for this object.
                </Form.Text>
              </Form.Group>

              {allowStandardObject && (
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="isStandard"
                    checked={formData.isStandard}
                    onChange={handleInputChange}
                    label="Standard Object"
                  />
                  <Form.Text className="text-muted">
                    When checked, this object will be marked as a standard object in the system.
                  </Form.Text>
                </Form.Group>
              )}
            </>
          )}

          {mode === 'edit' && nameFieldInfo && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Name Field Label</Form.Label>
                <Form.Control
                  type="text"
                  value={nameFieldInfo.label}
                  readOnly
                  className="bg-light"
                />
                <Form.Text className="text-muted">
                  The label of the name field (can be edited in Field Manager).
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Name Field Type</Form.Label>
                <Form.Control
                  type="text"
                  value={nameFieldInfo.dataType === 'autonumber' ? 'Auto Number' : 'Text'}
                  readOnly
                  className="bg-light"
                />
                <Form.Text className="text-muted">
                  Name field type cannot be changed after object creation.
                </Form.Text>
              </Form.Group>

              {nameFieldInfo.dataType === 'autonumber' && (
                <div className="autonumber-config mb-3 p-3 border rounded bg-light">
                  <h6 className="mb-3">Auto Number Configuration (Read-Only)</h6>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Start Number</Form.Label>
                        <Form.Control
                          type="text"
                          value={nameFieldInfo.startNumber || '1'}
                          readOnly
                          className="bg-white"
                        />
                        <Form.Text className="text-muted">
                          Initial number for auto-numbering
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Prefix</Form.Label>
                        <Form.Control
                          type="text"
                          value={nameFieldInfo.prefix ? nameFieldInfo.prefix : '(Not Set)'}
                          readOnly
                          className="bg-white"
                        />
                        <Form.Text className="text-muted">
                          Prefix added before the number
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Format Mask</Form.Label>
                        <Form.Control
                          type="text"
                          value={nameFieldInfo.formatMask ? nameFieldInfo.formatMask : '(Not Set)'}
                          readOnly
                          className="bg-white"
                        />
                        <Form.Text className="text-muted">
                          Number padding format (e.g., 0000)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Preview</Form.Label>
                        <Form.Control
                          type="text"
                          value={nameFieldInfo.preview || '1'}
                          readOnly
                          className="bg-white"
                        />
                        <Form.Text className="text-muted">
                          Example of generated number
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              )}
            </>
          )}

          {mode === 'edit' && (
            <>
              {/* Hide allowActivity, allowEmailLog, and allowNotes for email_log object */}
              {objectData?.object_name?.toLowerCase() !== 'email_log' && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      name="allowActivity"
                      checked={formData.allowActivity}
                      onChange={handleInputChange}
                      label="Allow Activity"
                      disabled={formData.allowActivity} // Disable if already checked
                    />
                    <Form.Text className="text-muted">
                      {formData.allowActivity
                        ? 'Activity tracking is already enabled for this object (includes Tasks, Notes, and Email Logs) and cannot be disabled.'
                        : 'When checked, this will enable activity tracking (Tasks, Notes, and Email Logs) for this object.'
                      }
                    </Form.Text>
                  </Form.Group>
                </>
              )}

              {allowStandardObject && (
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="isStandard"
                    checked={formData.isStandard}
                    onChange={handleInputChange}
                    label="Standard Object"
                  />
                  <Form.Text className="text-muted">
                    When checked, this object will be marked as a standard object in the system.
                  </Form.Text>
                </Form.Group>
              )}
            </>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              {mode === 'create' ? 'Creating...' : 'Updating...'}
            </>
          ) : (
            mode === 'create' ? 'Create Object' : 'Save'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditObject;
